/*
 * https://gist.github.com/EQuimper/42e86abac2ee23143a1f6094ef960b87
 * Based on EQuimper solution.
 */

import { useEffect, useState } from 'react';
import Router, { NextRouter } from 'next/router';
import { useEffectOnce, useSessionStorage } from 'react-use';

// Save the scroll position for the given url
const saveScrollPosition = (savePosition: (url: string, pos: number) => void) => {
    if (window) {
        const { pathname } = window.location;
        savePosition(pathname, window.scrollY);
    }
};

// Restore the scroll position for the given url is possible
const restoreScrollPosition = (positions: { [key: string]: number }) => {
    const { pathname } = window.location;

    if (positions) {
        const position = positions[`${pathname}`];
        if (position) {
            window.scrollTo(0, position);
        }
    }
};

// Restore the scroll position for the given url is possible
const getScrollPosition = (positions: { [key: string]: number }) => {
    if (positions) {
        const { pathname } = window.location;
        const position = positions[`${pathname}`];
        if (position) {
            return position;
        }
    }

    return 0;
};

let shouldScrollRestore = false;
let bodyHeightObserver = <ResizeObserver | undefined>undefined;

const useScrollRestoration = (router: NextRouter) => {
    const [positions, setPositions] = useState<{ [key: string]: number }>({});
    const [scrollPositionsStored, setScrollPositionsStored] = useSessionStorage<string>(
        'scroll-positions',
        '{}'
    );

    // Update list of saved scroll positions
    const updatePosition = (url: string, pos: number) => {
        const newPositions = {
            ...positions,
            [url]: pos,
        };
        setPositions(newPositions);
        setScrollPositionsStored(JSON.stringify(newPositions));
    };

    useEffectOnce(() => {
        const onRouteChangeComplete = () => {
            if (shouldScrollRestore) {
                // When route change complete create resize observer of body
                bodyHeightObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
                    const contentHeight = entries[0].contentRect.height;
                    const scrollPositions = JSON.parse(scrollPositionsStored);
                    const scrollToPosition = getScrollPosition(scrollPositions);

                    // if content size is bigger than position to scroll then scroll
                    if (contentHeight >= scrollToPosition && shouldScrollRestore) {
                        shouldScrollRestore = false;

                        // remove obvserver
                        bodyHeightObserver?.disconnect();
                        bodyHeightObserver = undefined;
                        try {
                            restoreScrollPosition(scrollPositions);
                        } catch (error) {
                            console.info('Unable to Restore Position');
                        }
                    }
                });

                const rootElem = document.getElementsByTagName('main')[0];
                if (rootElem) {
                    bodyHeightObserver.observe(rootElem);
                }
            }
        };
        onRouteChangeComplete();
    });

    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';

            const onBeforeUnload = (event: BeforeUnloadEvent) => {
                saveScrollPosition(updatePosition);
                delete event['returnValue'];
            };

            const onRouteChangeStart = () => {
                saveScrollPosition(updatePosition);
            };

            window.addEventListener('beforeunload', onBeforeUnload);
            Router.events.on('routeChangeStart', onRouteChangeStart);

            // for this route on pop state set shouldScrollRestore to true
            router.beforePopState(() => {
                shouldScrollRestore = true;
                return true;
            });

            // Clean listeners on unmount
            return () => {
                window.removeEventListener('beforeunload', onBeforeUnload);
                Router.events.off('routeChangeStart', onRouteChangeStart);
                bodyHeightObserver?.disconnect();
                bodyHeightObserver = undefined;
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};

export default useScrollRestoration;
